import { useSearchParams } from 'react-router-dom'
import { oauthProblemReasonSchema } from '@lib/dto/oauth.dto'
import { BrandLockup } from '@/components/BrandLockup'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

/** What the page says. A reason the server did not send is not shown. */
export function connectProblemMessage(reason: string | null): string {
  const parsed = oauthProblemReasonSchema.safeParse(reason)
  return parsed.success ? parsed.data : 'That request could not be understood.'
}

/**
 * Where an authorization goes when there is nowhere safe to send it back to.
 *
 * `/oauth/authorize` refuses an unknown application, or one asking to be sent
 * somewhere it never registered, *before* it will redirect anything — because
 * redirecting a failure to an unvalidated address is how an authorization
 * server becomes an open redirect that phishes from this very domain. So the
 * refusal comes here, to a page this app owns, rather than to the caller.
 *
 * Public: somebody reaching it has not signed in and should not be made to.
 */
export function ConnectProblemPage() {
  const [params] = useSearchParams()
  // The API's own words, from a fixed set of refusals. Anything else in the
  // query — which anybody can write — is not those words, so it is not shown.
  const reason = connectProblemMessage(params.get('reason'))

  return (
    <div className="flex min-h-svh items-center justify-center bg-surface-sunken/30 p-4">
      <div className="w-full max-w-lg space-y-6">
        <BrandLockup className="justify-center" />
        <Card size="lg">
          <CardHeader>
            <CardTitle>That connection can’t be set up</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-ink-muted">
            <p>{reason ?? 'That request could not be understood.'}</p>
            <p>
              Nothing has been shared and nothing has changed. If you were expecting this, start
              again from the application. If it keeps happening, the application is asking for
              access we can’t give it, and its makers are the ones to ask.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
